// Serializer for Announcement API to handle data transformation between frontend and Flask backend

/**
 * Serialize announcement data for API requests and responses
 */
class AnnouncementSerializer {
  fromJSON(apiData) {
    const announcement = apiData.data || apiData;

    if (!announcement) {
      return null;
    }

    const frontendAnnouncement = {
      id: announcement.id,
      title: announcement.title || "",
      description: announcement.description || "",
      targetGroup: announcement.target_group || "all",
      fullName: announcement.posted_by_fullname || "",
      postedBy: announcement.created_by || "",
      postedById: announcement.created_by || "",
      date: announcement.created_at
        ? this._formatDateForFrontend(announcement.created_at)
        : "",
      isPinned: announcement.is_pinned || false,
      imageUrl: announcement.image_url || null,

      // Additional fields for compatibility
      created_by: announcement.created_by,
      updated_by: announcement.updated_by,
      created_at: announcement.created_at,
      updated_at: announcement.updated_at,

      metadata: {
        created_at: announcement.created_at,
        updated_at: announcement.updated_at,
        created_by: announcement.created_by,
        updated_by: announcement.updated_by,
      },
    };

    return frontendAnnouncement;
  }

  toJSON(frontendData) {
    const backendAnnouncement = {
      title: frontendData.title || "",
      description: frontendData.description || "",
      target_group: frontendData.targetGroup || "all",
      posted_by_fullname: frontendData.fullName || "",
      is_pinned: frontendData.isPinned || false,
      created_by: frontendData.created_by || frontendData.postedBy,
      updated_by: frontendData.updated_by,
    };

    // Handle image_url specially
    if ("imageUrl" in frontendData || "image_url" in frontendData) {
      backendAnnouncement.image_url =
        frontendData.imageUrl || frontendData.image_url || null;
    }

    Object.keys(backendAnnouncement).forEach((key) => {
      if (key === "image_url") return;

      if (
        backendAnnouncement[key] === null ||
        backendAnnouncement[key] === undefined
      ) {
        delete backendAnnouncement[key];
      }
    });

    return backendAnnouncement;
  }

  /**
   * Convert date string from backend format to frontend format
   */
  _formatDateForFrontend(backendDate) {
    if (!backendDate) return "";

    try {
      const date = new Date(backendDate);
      if (isNaN(date.getTime())) {
        return "";
      }

      // Format as MM/DD/YYYY to match the Post component expectation
      return date.toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
      });
    } catch (error) {
      return "";
    }
  }

  /**
   * Convert date string from frontend format to backend format
   */
  _formatDateForBackend(frontendDate) {
    if (!frontendDate) return null;

    try {
      const date = new Date(frontendDate);
      if (isNaN(date.getTime())) {
        return null;
      }

      return date.toISOString();
    } catch (error) {
      return null;
    }
  }

  /**
   * Transform search response format
   */
  fromSearchJSON(searchResponse) {
    if (!searchResponse || !searchResponse.data) {
      return { hits: [], total: 0 };
    }

    const data = searchResponse.data;

    if (Array.isArray(data)) {
      return {
        hits: data.map((item) => this.fromJSON(item)),
        total: data.length,
      };
    }

    if (data.hits && Array.isArray(data.hits)) {
      return {
        hits: data.hits.map((item) => this.fromJSON(item)),
        total: data.total || data.hits.length,
      };
    }

    if (data.data && Array.isArray(data.data)) {
      return {
        hits: data.data.map((item) => this.fromJSON(item)),
        total: data.total || data.data.length,
      };
    }

    return { hits: [], total: 0 };
  }

  /**
   * Validate announcement data before sending to backend
   */
  validateAnnouncementData(data) {
    const errors = {};

    if (!data.title || !data.title.trim()) {
      errors.title = "Title is required";
    } else if (data.title.trim().length > 255) {
      errors.title = "Title must be 255 characters or less";
    }

    if (!data.description || !data.description.trim()) {
      errors.description = "Description is required";
    }

    if (!data.targetGroup) {
      errors.targetGroup = "Target group is required";
    } else if (
      ![
        "all",
        "student",
        "faculty",
        "librarian",
        "admin",
        "visitor",
        "researcher",
      ].includes(data.targetGroup)
    ) {
      errors.targetGroup = "Invalid target group";
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  }

  /**
   * Get display name for target group
   */
  getTargetGroupDisplayName(targetGroup) {
    const displayNames = {
      all: "All Users",
      student: "Students",
      faculty: "Faculty",
      librarian: "Librarians",
      admin: "Administrators",
      visitor: "Visitors",
      researcher: "Researchers",
    };

    return displayNames[targetGroup] || targetGroup;
  }

  /**
   * Filter announcements based on user role (client-side backup)
   */
  filterAnnouncementsByRole(announcements, userRole) {
    if (!Array.isArray(announcements)) return [];

    const role = userRole?.toLowerCase();

    // Admins and librarians can see all
    if (["admin", "librarian"].includes(role)) {
      return announcements;
    }

    // Filter for regular users
    return announcements.filter((announcement) => {
      const targetGroup = announcement.targetGroup?.toLowerCase();

      // Show if targeted to all users
      if (targetGroup === "all") return true;

      // Show if targeted to user's specific role
      if (targetGroup === role) return true;

      // Handle default role (users without assigned role) - show student announcements
      if ((!role || role === "patron") && targetGroup === "student")
        return true;

      return false;
    });
  }

  /**
   * Sort announcements with pinned first, then by date
   */
  sortAnnouncements(announcements) {
    if (!Array.isArray(announcements)) return [];

    return [...announcements].sort((a, b) => {
      // First, sort by pinned status (pinned first)
      if (a.isPinned && !b.isPinned) return -1;
      if (!a.isPinned && b.isPinned) return 1;

      // Then sort by date (newest first)
      const dateA = new Date(a.created_at || a.date);
      const dateB = new Date(b.created_at || b.date);
      return dateB - dateA;
    });
  }

  /**
   * Check if user can edit announcement
   */
  canUserEditAnnouncement(announcement, currentUser) {
    if (!currentUser || !announcement) return false;

    const userRoles = Array.isArray(currentUser.roles)
      ? currentUser.roles
      : [currentUser.roles];
    const userRole = userRoles.find((role) => role)?.toLowerCase();

    // Admins can edit all
    if (["admin"].includes(userRole)) return true;

    // Librarians can edit their own posts
    if (["librarian"].includes(userRole)) {
      return (
        announcement.postedById === currentUser.id ||
        announcement.postedById === currentUser.username ||
        announcement.created_by === currentUser.username ||
        announcement.created_by === currentUser.email
      );
    }

    return false;
  }

  /**
   * Check if user can post announcements
   */
  canUserPost(currentUser) {
    if (!currentUser) return false;

    const userRoles = Array.isArray(currentUser.roles)
      ? currentUser.roles
      : [currentUser.roles];

    return userRoles.some((role) =>
      ["admin", "librarian"].includes(role?.toLowerCase())
    );
  }

  /**
   * Convert file to base64 data URL
   */
  fileToDataUrl(file) {
    return new Promise((resolve, reject) => {
      if (!file) {
        resolve(null);
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  }
}

export const serializer = new AnnouncementSerializer();
export default AnnouncementSerializer;
